import { useEffect, useMemo, useState } from 'react';
import { usePipelines, useCreatePipeline, useDeletePipeline, useBulkDeletePipelines, Pipeline } from '@/hooks/useDeals';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/hooks/use-toast';
import { Card } from '@/components/ui/card';
import { Loader2, Plus, Trash2, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function SuperAdminPipelines() {
  type CheckedState = boolean | 'indeterminate';
  const currentUser = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  // Guard: only super_admin
  useEffect(() => {
    if (currentUser?.role !== 'super_admin') {
      navigate('/dashboard');
    }
  }, [currentUser?.role, navigate]);

  const { data: pipelines, isLoading, refetch } = usePipelines();
  const createMutation = useCreatePipeline();
  const deleteMutation = useDeletePipeline();
  const bulkDeleteMutation = useBulkDeletePipelines();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isGlobal, setIsGlobal] = useState(true);

  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[Number(k)]).map(Number), [selected]);
  const allSelected = useMemo(() => (pipelines?.length ? selectedIds.length === pipelines.length : false), [pipelines, selectedIds.length]);

  const toggleAll = () => {
    if (!pipelines?.length) return;
    const next: Record<number, boolean> = {};
    const setTo = !allSelected;
    pipelines.forEach(p => { next[p.id] = setTo; });
    setSelected(next);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast({ title: 'Nom requis', description: "Veuillez entrer un nom pour le pipeline.", variant: 'destructive' });
      return;
    }
    try {
      await createMutation.mutateAsync({ name: name.trim(), description: description.trim() || undefined, is_global: isGlobal });
      setName('');
      setDescription('');
      setIsGlobal(true);
      toast({ title: 'Pipeline créé', description: isGlobal ? 'Pipeline global disponible pour tous.' : 'Pipeline créé.' });
    } catch (e: any) {
      toast({ title: 'Erreur lors de la création', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Supprimer ce pipeline et toutes ses données (deals, tâches, activités, etc.) ?')) return;
    try {
      await deleteMutation.mutateAsync(id);
      toast({ title: 'Pipeline supprimé' });
    } catch (e: any) {
      toast({ title: 'Erreur lors de la suppression', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!confirm(`Supprimer ${selectedIds.length} pipeline(s) et toutes leurs données ?`)) return;
    try {
      await bulkDeleteMutation.mutateAsync(selectedIds);
      setSelected({});
      toast({ title: 'Pipelines supprimés' });
    } catch (e: any) {
      toast({ title: 'Erreur lors de la suppression', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="w-5 h-5 text-purple-500" /> Gestion des Pipelines (Super Admin)
        </h1>
        <div className="flex gap-2">
          <Button variant="destructive" disabled={!selectedIds.length || bulkDeleteMutation.isPending} onClick={handleBulkDelete}>
            {bulkDeleteMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Supprimer la sélection ({selectedIds.length})
          </Button>
          <Button variant="outline" onClick={() => refetch()}>Rafraîchir</Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-3">
            <label className="block text-sm text-slate-500 mb-1">Nom</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom du pipeline" />
          </div>
          <div className="md:col-span-6">
            <label className="block text-sm text-slate-500 mb-1">Description</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optionnel)" />
          </div>
          <div className="md:col-span-2 flex items-center gap-2">
            <Checkbox id="isGlobal" checked={isGlobal} onCheckedChange={(v: CheckedState) => setIsGlobal(!!v)} />
            <label htmlFor="isGlobal" className="text-sm">Global (visible par tous)</label>
          </div>
          <div className="md:col-span-1">
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="border-b px-4 py-3 flex items-center gap-3 text-sm text-slate-600">
          <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
          <div className="w-10 text-slate-500">ID</div>
          <div className="flex-1">Nom</div>
          <div className="w-1/3 hidden md:block">Description</div>
          <div className="w-40">Portée</div>
          <div className="w-40 text-right pr-2">Actions</div>
        </div>
        {isLoading ? (
          <div className="p-6 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement des pipelines...
          </div>
        ) : (
          <div>
            {pipelines?.length ? pipelines.map((p: Pipeline) => {
              const isSel = !!selected[p.id];
              return (
                <div key={p.id} className="px-4 py-3 border-b flex items-center gap-3 text-sm">
                  <Checkbox checked={isSel} onCheckedChange={(v: CheckedState) => setSelected((s) => ({ ...s, [p.id]: !!v }))} />
                  <div className="w-10 text-slate-500">{p.id}</div>
                  <div className="flex-1 font-medium">{p.name}</div>
                  <div className="w-1/3 hidden md:block truncate text-slate-600">{p.description || '-'}</div>
                  <div className="w-40">
                    {p.created_by == null ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">Global</span>
                    ) : (
                      <span className="text-slate-600">Privé</span>
                    )}
                  </div>
                  <div className="w-40 text-right">
                    <Button size="sm" variant="destructive" aria-label={`Supprimer ${p.name}`} onClick={() => handleDelete(p.id)} disabled={deleteMutation.isPending}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            }) : (
              <div className="p-6 text-center text-slate-500">Aucun pipeline pour le moment.</div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
